import type { AxiosError } from "axios";
import type { BaseResponse } from "./api-contract";

export enum ResultCode {
  Success = 0,
  ValidationError = 1,
  Unauthorized = 2,
  Forbidden = 3,
  NotFound = 4,
  Conflict = 5,
  Error = 6,
  AuthEmailAlreadyExists = 1001,
  AuthInvalidCredentials = 1002,
  AuthEmailNotVerified = 1003,
  AuthUserNotFound = 1004,
  AuthInvalidVerificationCode = 1005,
  AuthVerificationCodeExpired = 1006,
  AuthInvalidResetCode = 1007,
  AuthResetCodeExpired = 1008,
  AuthInvalidRefreshToken = 1009,
  AuthUnsupportedLoginProvider = 1010,
  AuthSocialAccountNotRegistered = 1011,
  AuthOrganizationNameNotFound = 1012,
  AuthLoginWithEmailPassword = 1013,
  AuthLoginWithGoogle = 1014,
  AuthLoginWithApple = 1015,
  MerchantUserCreated = 2001,
  MerchantEmailVerified = 2002,
  MerchantCreated = 2003,
  MerchantSocialAccountConnected = 2004,
  LocalMerchantNotFound = 2005,
  AttendanceRecordAlreadyExists = 3001,
  AttendanceEventNotFound = 3002,
  AttendanceSignInOutsideEventTime = 3003,
  AttendanceCheckInNotOpen = 3004,
  SurveyPublishedCannotDelete = 4001,
  SurveyHasResponsesCannotDelete = 4002,
}

export const resultCodeMessages: Readonly<Record<number, string>> = {
  [ResultCode.Success]: "Success.",
  [ResultCode.ValidationError]: "Validation failed.",
  [ResultCode.Unauthorized]: "Unauthorized.",
  [ResultCode.Forbidden]: "Forbidden.",
  [ResultCode.NotFound]: "Resource not found.",
  [ResultCode.Conflict]: "A conflict occurred.",
  [ResultCode.Error]: "An unexpected error occurred.",
  [ResultCode.AuthEmailAlreadyExists]: "Email already exists.",
  [ResultCode.AuthInvalidCredentials]: "Invalid login credentials.",
  [ResultCode.AuthEmailNotVerified]: "Email is not verified.",
  [ResultCode.AuthUserNotFound]: "User was not found.",
  [ResultCode.AuthInvalidVerificationCode]: "Invalid verification code.",
  [ResultCode.AuthVerificationCodeExpired]: "Verification code has expired.",
  [ResultCode.AuthInvalidResetCode]: "Invalid reset code.",
  [ResultCode.AuthResetCodeExpired]: "Reset code has expired.",
  [ResultCode.AuthInvalidRefreshToken]: "Invalid refresh token.",
  [ResultCode.AuthUnsupportedLoginProvider]: "Unsupported login provider.",
  [ResultCode.AuthSocialAccountNotRegistered]: "Social account is not registered.",
  [ResultCode.AuthOrganizationNameNotFound]: "Organization name was not found.",
  [ResultCode.AuthLoginWithEmailPassword]: "Please log in with email and password.",
  [ResultCode.AuthLoginWithGoogle]: "Please log in with Google.",
  [ResultCode.AuthLoginWithApple]: "Please log in with Apple.",
  [ResultCode.MerchantUserCreated]: "Merchant user created successfully.",
  [ResultCode.MerchantEmailVerified]: "Merchant email verified successfully.",
  [ResultCode.MerchantCreated]: "Merchant created successfully.",
  [ResultCode.MerchantSocialAccountConnected]: "Merchant social account connected successfully.",
  [ResultCode.LocalMerchantNotFound]: "Local merchant was not found.",
  [ResultCode.AttendanceRecordAlreadyExists]: "Attendance record already exists.",
  [ResultCode.AttendanceEventNotFound]: "Event was not found.",
  [ResultCode.AttendanceSignInOutsideEventTime]:
    "Sign in is only allowed between start_time and end_time.",
  [ResultCode.AttendanceCheckInNotOpen]: "Sign in is only allowed on or after check_in_time.",
  [ResultCode.SurveyPublishedCannotDelete]: "Published surveys cannot be deleted.",
  [ResultCode.SurveyHasResponsesCannotDelete]: "Surveys with responses cannot be deleted.",
};

export class ApiResultError extends Error {
  readonly code?: number;
  readonly response?: BaseResponse<unknown>;

  constructor(message: string, code?: number, response?: BaseResponse<unknown>) {
    super(message);
    this.name = "ApiResultError";
    this.code = code;
    this.response = response;
  }
}

type ErrorPayload = Partial<BaseResponse<unknown>> & { code?: number };

function getPayload(error: unknown): ErrorPayload | undefined {
  if (error instanceof ApiResultError) return error.response ?? { code: error.code };
  const axiosError = error as AxiosError<ErrorPayload>;
  return axiosError.response?.data;
}

export function getResultCode(error: unknown): number | undefined {
  const payload = getPayload(error);
  const code = payload?.code ?? payload?.errors?.[0]?.code;
  return typeof code === "number"
    ? code
    : typeof code === "string" && /^\d+$/.test(code)
      ? Number(code)
      : undefined;
}

export function getResultCodeMessage(code?: number): string | undefined {
  return code === undefined ? undefined : resultCodeMessages[code];
}

export function getApiErrorMessage(
  error: unknown,
  fallback = "An unexpected error occurred.",
): string {
  const payload = getPayload(error);
  return (
    payload?.user_message ||
    payload?.message ||
    getResultCodeMessage(getResultCode(error)) ||
    (error instanceof Error ? error.message : fallback)
  );
}

export function normalizeApiError<T>(
  error: unknown,
  fallback = "An unexpected error occurred.",
): BaseResponse<T> {
  if (error instanceof ApiResultError && error.response) {
    return error.response as BaseResponse<T>;
  }

  const code = getResultCode(error) ?? ResultCode.Error;
  const message = getApiErrorMessage(error, fallback);
  const axiosError = error as AxiosError;

  return {
    success: false,
    code,
    http_request_status: axiosError.response?.status ?? 500,
    message,
    user_message: message,
    data: null,
    errors: [],
  };
}

export function createApiResultError<T>(
  response: BaseResponse<T>,
  fallback?: string,
): ApiResultError {
  const code = response.code ?? response.errors?.[0]?.code;
  const numericCode =
    typeof code === "number"
      ? code
      : typeof code === "string" && /^\d+$/.test(code)
        ? Number(code)
        : undefined;
  return new ApiResultError(
    response.user_message ||
      response.message ||
      getResultCodeMessage(numericCode) ||
      fallback ||
      "Request failed",
    numericCode,
    response as BaseResponse<unknown>,
  );
}
